/**
 * <form-builder>
 *
 * Companion script to blocks/form-builder.liquid. Handles AJAX
 * submission for the three non-Shopify form modes (Shopify mode is
 * server-rendered and needs no JS):
 *
 *   formspark — POSTs FormData to the merchant's Formspark endpoint.
 *   klaviyo   — Reads company_id + g (list id) hidden inputs, builds
 *               a Client-side API JSON payload, POSTs to
 *               https://a.klaviyo.com/client/subscriptions/.
 *               Modelled on v3.0's handleKlaviyoSubscription flow.
 *   custom    — POSTs FormData to the merchant-supplied URL.
 *
 * reCAPTCHA v2 invisible: when the form-builder block has a site key
 * set, the Liquid emits a <div class="g-recaptcha"> placeholder. This
 * module programmatically renders the widget into that div on connect
 * (waiting for the async-loaded api.js to expose `grecaptcha`), then
 * on submit calls grecaptcha.execute() and awaits the token via a
 * Promise. Token is attached as a hidden `g-recaptcha-response` input
 * before the request goes out, and the widget is reset after every
 * submission so back-to-back submits work.
 *
 * Success / error UX: the Liquid pre-renders two divs
 *   [data-form-message="success"]    [data-form-message="error"]
 * both hidden by default. This module toggles `hidden` to show the
 * appropriate one after each submit. The form is reset on success.
 *
 * Loading state: submit button is disabled and gets a `.is-loading`
 * class for the duration of the request so the merchant's CSS (or
 * the sa-cta spinner once it lands) can show a loader.
 */

class FormBuilder extends HTMLElement {
  connectedCallback() {
    this.formType = this.dataset.formType || 'shopify';

    // Shopify mode is server-rendered via {% form 'contact' %}. The
    // browser handles the POST natively and Shopify renders success /
    // error on reload — no JS hook needed.
    if (this.formType === 'shopify') return;

    this.form = this.querySelector('.form-builder__form');
    if (!(this.form instanceof HTMLFormElement)) return;

    this.submitButton = this.querySelector('.form-submit');
    this.successMessage = this.querySelector('[data-form-message="success"]');
    this.errorMessage = this.querySelector('[data-form-message="error"]');
    this.recaptchaContainer = this.querySelector('.g-recaptcha');

    if (this.recaptchaContainer) this.#initRecaptcha();

    this.form.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#handleSubmit);
  }

  // ----- reCAPTCHA -----------------------------------------------------------

  #recaptchaWidgetId;
  #pendingRecaptchaResolve = null;

  /**
   * Renders the reCAPTCHA widget into the placeholder div. api.js loads
   * async/defer so we poll for window.grecaptcha and call its `ready`
   * helper before render. The callback assigned here closes over
   * this.#pendingRecaptchaResolve, which is (re)bound for every submit
   * — that's how the per-submit token promise gets resolved.
   */
  #initRecaptcha() {
    const sitekey = this.recaptchaContainer.dataset.sitekey;
    if (!sitekey) return;

    const tryRender = () => {
      const g = /** @type {any} */ (window).grecaptcha;
      if (!g || typeof g.render !== 'function') {
        setTimeout(tryRender, 100);
        return;
      }
      g.ready(() => {
        try {
          this.#recaptchaWidgetId = g.render(this.recaptchaContainer, {
            sitekey,
            size: 'invisible',
            callback: (token) => {
              const resolve = this.#pendingRecaptchaResolve;
              this.#pendingRecaptchaResolve = null;
              resolve?.(token);
            },
            'expired-callback': () => {
              const resolve = this.#pendingRecaptchaResolve;
              this.#pendingRecaptchaResolve = null;
              resolve?.(null);
            },
            'error-callback': () => {
              const resolve = this.#pendingRecaptchaResolve;
              this.#pendingRecaptchaResolve = null;
              resolve?.(null);
            },
          });
        } catch (e) {
          console.error('reCAPTCHA render failed:', e);
        }
      });
    };
    tryRender();
  }

  #executeRecaptcha() {
    if (this.#recaptchaWidgetId === undefined) return Promise.resolve(null);
    return new Promise((resolve) => {
      this.#pendingRecaptchaResolve = resolve;
      /** @type {any} */ (window).grecaptcha.execute(this.#recaptchaWidgetId);
    });
  }

  #resetRecaptcha() {
    if (this.#recaptchaWidgetId === undefined) return;
    /** @type {any} */ (window).grecaptcha?.reset?.(this.#recaptchaWidgetId);
  }

  // ----- Submit flow ---------------------------------------------------------

  #handleSubmit = async (event) => {
    event.preventDefault();
    this.#setLoading(true);
    this.#hideMessages();

    try {
      if (this.recaptchaContainer) {
        const token = await this.#executeRecaptcha();
        if (!token) {
          this.#showError();
          return;
        }
        this.#setHiddenInput('g-recaptcha-response', token);
      }

      const response = this.formType === 'klaviyo'
        ? await this.#submitToKlaviyo()
        : await this.#submitGeneric();

      if (response.ok) {
        this.#showSuccess();
        this.form.reset();
      } else {
        this.#showError();
      }
    } catch (error) {
      console.error('Form submission error:', error);
      this.#showError();
    } finally {
      this.#setLoading(false);
      this.#resetRecaptcha();
    }
  };

  #submitGeneric() {
    return fetch(this.form.action, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(this.form),
    });
  }

  /**
   * Klaviyo Client-side API submission. The Liquid injected
   * company_id + g hidden inputs from block settings; everything else
   * in the form is treated as a profile attribute (matches v3.0's
   * subscriber field flow). Required fields per Klaviyo's spec are
   * email or phone_number — we pass whatever the merchant added in
   * the form-builder.
   */
  async #submitToKlaviyo() {
    const data = Object.fromEntries(new FormData(this.form).entries());
    const companyId = data.company_id;
    const listId = data.g;
    delete data.company_id;
    delete data.g;
    delete data['g-recaptcha-response'];

    if (!companyId || !listId) {
      throw new Error('Klaviyo requires both Public API Key (company_id) and List ID (g) — set them on the Form Builder block.');
    }

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Form Builder',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                ...data,
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } },
                },
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: listId } },
        },
      },
    };

    return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        revision: '2025-04-15',
        'content-type': 'application/vnd.api+json',
      },
      body: JSON.stringify(payload),
    });
  }

  // ----- UI helpers ----------------------------------------------------------

  #setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.classList.toggle('is-loading', isLoading);
  }

  #hideMessages() {
    if (this.successMessage) this.successMessage.hidden = true;
    if (this.errorMessage) this.errorMessage.hidden = true;
  }

  #showSuccess() {
    if (this.successMessage) this.successMessage.hidden = false;
  }

  #showError() {
    if (this.errorMessage) this.errorMessage.hidden = false;
  }

  /**
   * Set or create a hidden input on the form. Used to attach the
   * reCAPTCHA token right before submission — easier than appending
   * to FormData after construction.
   */
  #setHiddenInput(name, value) {
    let input = this.form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      this.form.appendChild(input);
    }
    input.value = value;
  }
}

if (!customElements.get('form-builder')) {
  customElements.define('form-builder', FormBuilder);
}
